// Angular Files
import { Injectable } from '@angular/core';

// Teller Online Core Files
import {
    SiteCustomizationDto,
    AppConfigurationDto,
    SiteMetadataDto,
    MenuItemDto,
    PortalFeatureDto,
    SiteMetadataApiClient,
    FlagTypeEnumDto,
    NoticeTypeEnumDto,
    NoticeDto
} from '../api/CoreWebApi';

// Teller Online Services
import { TellerOnlineNotificationBanner, TellerOnlineNotificationBannerService } from './notification-banner.service';

@Injectable({
    providedIn: 'root'
})
export class TellerOnlineSiteMetadataService {
    private _metadata!: SiteMetadataDto;

    loaded: boolean = false;

    static features: {
        authenticatedUsers: PortalFeatureDto,
        tellerOnlineLite: PortalFeatureDto,
        scheduledPayments: PortalFeatureDto
    };

    constructor(
        private siteMetadataApiClient: SiteMetadataApiClient,
        private notificationBannerService: TellerOnlineNotificationBannerService
    ) { }

    init(baseUrl: string) {
        SiteMetadataApiClient.init(baseUrl);
    }

    public async load() {
        this._metadata = await this.siteMetadataApiClient.getSiteMetadata().toPromise();
        this.loaded = true;
    }

    public get appConfiguration(): AppConfigurationDto {
        if (!this._metadata) {
            throw new Error('Site metadata is not loaded!');
        }

        return this._metadata.appConfiguration;
    }

    /**
     * When true, the payment authorization disclosure is presented in a modal;
     * when false (default) it is shown inline above the submit button.
     */
    public get presentConsentInModal(): boolean {
        return this._metadata?.appConfiguration?.presentConsentInModal ?? false;
    }

    /**
     * When true, paying with a saved credit card requires per-payment
     * authorization consent; when false (default) the save-time authorization
     * covers future payments.
     */
    public get requireSavedMethodReauthorizationCreditCard(): boolean {
        return this._metadata?.appConfiguration?.requireSavedMethodReauthorizationCreditCard ?? false;
    }

    /**
     * When true, paying with a saved eCheck requires per-payment authorization
     * consent; when false (default) the save-time authorization covers future
     * payments.
     */
    public get requireSavedMethodReauthorizationECheck(): boolean {
        return this._metadata?.appConfiguration?.requireSavedMethodReauthorizationECheck ?? false;
    }

    /**
     * Maximum size, in bytes, allowed for a single uploaded attachment,
     * or undefined when the site defines no cap.
     */
    public get maxUploadFileSizeBytes(): number | undefined {
        return this._metadata?.appConfiguration?.maxUploadFileSizeBytes;
    }

    public get portalFeatures(): { authenticatedUsers: PortalFeatureDto, tellerOnlineLite: PortalFeatureDto, scheduledPayments: PortalFeatureDto } {
        if (!this._metadata) {
            throw new Error('Site metadata is not loaded!');
        }

        if (!TellerOnlineSiteMetadataService.features) {
            const features = {
                authenticatedUsers: null,
                tellerOnlineLite: null,
                scheduledPayments: null
            };

            // Change from an array to an key value list so the items can be referenced by name rather than index
            this._metadata.portalFeatures.forEach(feature => {
                features[feature.name.replace(feature.name[0], feature.name[0].toLowerCase())] = feature;
            });

            TellerOnlineSiteMetadataService.features = features;
        }

        return TellerOnlineSiteMetadataService.features;
    }

    public get customization(): SiteCustomizationDto {
        if (!this._metadata) {
            // throw new Error("Site metadata is not loaded!");
            return null;
        }

        return this._metadata.siteCustomization;
    }

    public get menuItems(): MenuItemDto[] {
        if (!this._metadata) {
            // throw new Error("Site metadata is not loaded!");
            return null;
        }

        return this._metadata.menuItems;
    }

    public get authenticationEnabled(): boolean {
        return this.portalFeatures.authenticatedUsers?.enabled;
    }

    public get tellerOnlineLiteEnabled(): boolean {
        return this.portalFeatures.tellerOnlineLite?.enabled ?? false;
    }

    public get scheduledPaymentsEnabled(): boolean {
        return this.portalFeatures.scheduledPayments?.enabled ?? false;
    }

    public get tellerSiteTimeZoneId(): string {
        return this._metadata?.tellerSiteTimeZoneId ?? 'UTC';
    }

    public get tenant(): string {
        return this._metadata?.tenantName?.toLowerCase() ?? null;
    }

    /**
     * True when the tenant is usable in an api/assets/* path. Mirrors the
     * backend's AssetHelper.IsValidTenantName (^[a-z0-9]+$); also covers null,
     * which is normal for a site that is not white-labeled.
     */
    public get hasServableTenant(): boolean {
        const name = this.tenant;

        return !!name && /^[a-z0-9]+$/.test(name);
    }

    public get activeNotices() {
        const today = new Date();
        return this._metadata.notices.filter(n => n.startTimeUtc <= today && n.endTimeUtc > today);
    }

    public get upcomingNotices() {
        const today = new Date();

        return this._metadata.notices.filter(n => {
            const timeDifference = n.startTimeUtc.getTime() - today.getTime();
            // Ensure that the notice start time is less than a day in the future at most
            return timeDifference > 0 && timeDifference < 24 * 60 * 60 * 1000;
        });
    }

    public parseMenuNotices(menuItem: number, noticeType: FlagTypeEnumDto = null) {
        let activeMenuFlags = this.activeNotices.filter(
            n => n.integrationFlags.find(f => f.pageId === menuItem)
        );

        if (noticeType) {
            activeMenuFlags = activeMenuFlags.filter(
                n => n.integrationFlags.find(
                    f => f.pageId === menuItem && f.flagType === noticeType
                )
            );
        }

        return activeMenuFlags?.length > 0;
    }

    public createNoticeBanner(notice: NoticeDto) {
        const banner = new TellerOnlineNotificationBanner();
        const bannerCount = this.notificationBannerService.banners.length;
        banner.tag = (`notice-${bannerCount + 1}`).toString();
        banner.message = notice.message;
        banner.resolveAction = {
            text: ''
        };
        banner.dismissAction = {
            text: 'Dismiss',
            color: ''
        };

        switch (notice.noticeType) {
            case NoticeTypeEnumDto.Info:
                banner.bannerClass = 'notification-banner--info';
                banner.svgIcon = 'info';
                banner.svgIconClass = 'teller-online-icon--info';
                break;
            case NoticeTypeEnumDto.Warning:
                banner.bannerClass = 'notification-banner--caution';
                banner.svgIcon = 'attention';
                banner.svgIconClass = 'teller-online-icon--caution';
                break;
            case NoticeTypeEnumDto.Alert:
                banner.bannerClass = 'notification-banner--warn';
                break;
            default:
                break;
        }

        if (this.notificationBannerService.banners.length > 0) {
            // Replace 'Dismiss' with 'Next' on the action button of the final banner
            // in the list. The new banner will now have the 'Dismiss' text.
            const previousDismissBanner = this.notificationBannerService.banners.find(
                b => b.dismissAction.text === 'Dismiss'
            );
            this.notificationBannerService.updateBanner(
                previousDismissBanner.tag,
                { dismissAction: { text: 'Next' } }
            );
        }
        this.notificationBannerService.addBanner(banner);
    }
}
